import type { VpIconName } from '@vtmn-play/react'
import type { Toast, ToastVariant } from './types'
import { VpIcon, VpIconButton } from '@vtmn-play/react'
import { useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import './toaster.scss'

interface ToasterHostProps {
  toasts: Toast[]
  onDismiss: (id: string) => void
}

const variantConfig: Record<ToastVariant, { icon: VpIconName, role: 'status' | 'alert' }> = {
  success: { icon: 'check', role: 'status' },
  warning: { icon: 'warning-error', role: 'alert' },
  error: { icon: 'error', role: 'alert' },
  information: { icon: 'information', role: 'status' },
}

const statusTranslationKeys: Record<ToastVariant, string> = {
  success: 'toaster.status.success',
  warning: 'toaster.status.warning',
  error: 'toaster.status.error',
  information: 'toaster.status.information',
}

export function ToasterHost({ toasts, onDismiss }: Readonly<ToasterHostProps>) {
  const { t } = useTranslation()
  const toasterRef = useRef<HTMLElement>(null)

  useLayoutEffect(() => {
    const toaster = toasterRef.current
    if (!toaster || typeof toaster.showPopover !== 'function' || typeof toaster.hidePopover !== 'function')
      return

    const isOpen = toaster.matches(':popover-open')
    if (toasts.length > 0 && !isOpen)
      toaster.showPopover()
    else if (toasts.length === 0 && isOpen)
      toaster.hidePopover()
  }, [toasts.length])

  return (
    <section
      ref={toasterRef}
      className="toaster"
      popover="manual"
      aria-label={t('toaster.label')}
      onMouseDown={event => event.stopPropagation()}
    >
      {toasts.map((toast) => {
        const config = variantConfig[toast.variant]
        const message = toast.message

        return (
          <div
            className={`toaster__toast ${toast.variant}`}
            key={toast.id}
            role={config.role}
            aria-live={config.role === 'alert' ? 'assertive' : 'polite'}
            aria-atomic="true"
          >
            <VpIcon name={config.icon} size={24} />
            <div className="toaster__content">
              <p className="toaster__status">{t(statusTranslationKeys[toast.variant])}</p>
              {message && <p className="toaster__message">{message}</p>}
            </div>
            {toast.dismissible && (
              <VpIconButton
                aria-label={t('toaster.close')}
                size="small"
                variant="tertiary"
                onClick={() => onDismiss(toast.id)}
              >
                <VpIcon name="close" size={20} />
              </VpIconButton>
            )}
          </div>
        )
      })}
    </section>
  )
}
